import axios from 'axios';
import { FilterType } from 'bindings/filters/FilterType';
import { FieldType } from 'bindings/forms/FieldType';
import { ValueKey } from 'bindings/reports/generic/ValueKey';
import { Cookies } from 'react-cookie';
import { atom, selector } from 'recoil';
import { filterIdFromString, isAdditionalColumnFilter } from '../components_v2/filter/model/Model';
import { Group, GroupInsideType } from '../components_v2/Selector/actions';
import { AUsers } from './global/users';
import { URL_FOUNDATION } from '../config/keys';
import { Value } from '../containers_v2/reports/interpretor/bareReportingDecoder';
import { AdditionalColumnsDisplayOptions } from '../containers_v2/settings/subSettings/actions';
import { ViewCompanyColumns } from './global/views';
import { AtomCategory, AtomState } from './utils/model/Model';

export const ADDITIONAL_COLUMN_TYPE_LST = ['Integer', 'Number', 'String', 'Boolean', 'Date', 'Catalogue', 'ReportColumn', 'Company', 'Select', 'MultiSelect', 'Hours'] as const;
export type AdditionalColumnType = typeof ADDITIONAL_COLUMN_TYPE_LST[number];
export type ReportColumnType = Value['val'];
type FirstValueNotPieChart = {
	id: number,
	column_type: 'additional_column' | 'calculated_field'
	value: ReportColumnType
}

export function isAdditionalColumnTypeVisibleOnMap(type: AdditionalColumnType): boolean {
	switch (type) {
		case 'Boolean':
		case 'Catalogue':
		case 'Date':
		case 'Integer':
		case 'Number':
		case 'Company':
		case 'MultiSelect':
		case 'Select':
			return true;
	}
	return false;
}

export type AdditionalColumn = {
	id: number,
	name: string,
	ishidded?: boolean,
	locked: boolean,
	field_id?: number,
	display_options?: AdditionalColumnsDisplayOptions,
} & ({
	type: Exclude<AdditionalColumnType, 'Select' | 'MultiSelect'>,
	data: undefined,
} | { type: 'Select' | 'MultiSelect', data: string[] })

export type AdditionalFormFields = {
	field_id: number,
	value: string,
	name: string,
	field_type: FieldType,
}

export type CompanyAdditionalColumnValue = {
	additional_column_id: number,
	value: unknown,
	enriched_value?: string,

}

export function additionalColumnTypeToFilterTypeAndPath(type: AdditionalColumnType): { type: FilterType, path?: ValueKey } {
	switch (type) {
		case 'Integer': return { type: 'numeric' };
		case 'Number': return { type: 'numeric' };
		case 'Boolean': return { type: 'bool' };
		case 'Catalogue': return { type: 'catalogue' };
		case 'Date': return { type: 'temporal' };
		case 'String': return { type: 'string', path: 'text' };
		case 'ReportColumn': return { type: 'nullable' };
		case 'Company': return { type: 'company' };
		case 'Select': return { type: 'select' };
		case 'MultiSelect': return { type: 'multi_select' };
		case 'Hours': return { type: 'hours' };
	}

}

function updateCompanyHiddenColumns(cols: AdditionalColumn[]) {
	const companyId = new Cookies().get('company_id');
	if (!companyId) return;
	const allStored: Record<string, ViewCompanyColumns[]> = JSON.parse(localStorage.getItem('companies_hidden_columns') || '{}');
	const localColumns: ViewCompanyColumns[] = Array.isArray(allStored[companyId]) ? allStored[companyId] : [];
	if (localColumns.length == 0) return;
	const mapped = localColumns.map(lc => filterIdFromString(lc.column));
	cols.filter(c => !mapped.some(m => {
		if (isAdditionalColumnFilter(m)) return m.additional_columns.some(ac => ac.id == c.id);
		return false;
	})).forEach(c => localColumns.push({ column: `{"additional_columns":[{"id":${c.id}}]}`, hidden: true }));
	allStored[companyId] = localColumns;
	localStorage.setItem('companies_hidden_columns', JSON.stringify(allStored));
}

// -----------------------[ ATOM ]----------------------- //
const AGlobalAdditionalColumns: AtomState<AdditionalColumn[]> = {
	category: AtomCategory.GLOBAL,
	atom: atom({
		key: 'atom_global_additional_columns', // UNIQUE ID
		default: getAdditionalColumns()
	})
};

export const AAdditionalColumns = selector({
	key: 'AAdditionalColumns',
	get: async({ get }) => get(AGlobalAdditionalColumns.atom),
	set: ({ set }, newValue) => {
		if (Array.isArray(newValue)) updateCompanyHiddenColumns(newValue);
		set(AGlobalAdditionalColumns.atom, newValue);
	},
});

/// The fields this user may see, by kind, as the company sheet decides it.
/// Two separate things hide one, and only one of them is a flag on the field:
///  - unticked in "Champs visibles sur la fiche société" (ishidded), which
///    hides it for the whole account
///  - sitting in a category that is not public and is not shared with them,
///    which hides it for that user alone
/// The sheet is built from this very call, so reading it here is what makes a
/// filter list agree with the page people check it against.
///
/// Undefined means "do not filter": an account with no categories at all has
/// configured nothing, and hiding every field would be a worse answer than
/// hiding none. A field in no category is absent from the sheet too, so it is
/// absent here, and the two stay consistent.
export const AVisibleCompanyFieldIds = selector<Record<GroupInsideType, Set<number>> | undefined>({
	key: 'AVisibleCompanyFieldIds',
	get: async({ get }) => {
		const me = get(AUsers).find(u => u.isYou);
		if (!me) return undefined;
		const categories = await getUserCategories(me.id).catch(() => [] as Group[]);
		if (categories.length === 0) return undefined;
		const ids: Record<GroupInsideType, Set<number>> = {
			[GroupInsideType.AdditionalColumn]: new Set(),
			[GroupInsideType.CalculatedField]: new Set(),
			[GroupInsideType.FormField]: new Set(),
			[GroupInsideType.Report]: new Set(),
		};
		// ids are only unique within a kind: a column and a form field can both
		// be 5, and the categories hold all of them side by side
		categories.forEach(c => c.inside.filter(i => !i.ishidded).forEach(i => ids[i.type_]?.add(i.id)));
		return ids;
	},
});

/// Keeps what this user may see of a kind, by the rule above. Falls back to the
/// field's own flag when there are no categories to read.
export function visibleOf<T>(
	ids: Record<GroupInsideType, Set<number>> | undefined,
	kind: GroupInsideType,
	fields: T[],
	idOf: (field: T) => number,
	isHidden: (field: T) => boolean,
): T[] {
	return fields.filter(f => !isHidden(f) && (ids === undefined || ids[kind].has(idOf(f))));
}

/// Read this wherever a field is offered to the user; the unfiltered atom is
/// for the settings screen that does the hiding, and for writes.
export const AVisibleAdditionalColumns = selector({
	key: 'AVisibleAdditionalColumns',
	get: ({ get }) =>
		visibleOf(get(AVisibleCompanyFieldIds), GroupInsideType.AdditionalColumn, get(AAdditionalColumns), c => c.id, c => !!c.ishidded),
});

// -----------------------[ API ]------------------------ //
const cookies = new Cookies();
const token: string | null = cookies.get('id_token') || null;

axios.defaults.headers.common.Authorization = `${token ?? ''}`;

export async function getAdditionalColumns(): Promise<AdditionalColumn[]> {
	const cookies = new Cookies();
	const token: string | null = cookies.get('id_token') || null;

	axios.defaults.headers.common.Authorization = `${token ?? ''}`;
	return axios.get(`${URL_FOUNDATION}/api/v2/additional-columns`).then(res => res.data).catch(() => []);
}

export async function getAdditionnalFormFields(companyId: number): Promise<AdditionalFormFields[]> {
	return axios.get(`${URL_FOUNDATION}/api/v2/additional-columns/form-fields/${companyId}`).then(res => res.data);
}

export async function updateAdditionalColumnValue(columnId: number, companyId: number, value: undefined | string | number | object | null | boolean) {
	return axios.put(`${URL_FOUNDATION}/api/v2/additional-columns/${columnId}/${companyId}`, { value }, {
		headers: {
			'Content-Type': 'application/json'
		}
	}).then(res => res.data);
}

export async function deleteAdditionalColumnValue(columnId: number, companyId: number) {
	return axios.delete(`${URL_FOUNDATION}/api/v2/additional-columns/${columnId}/${companyId}`).then(res => res.data);
}

export async function getFirstValueNotPieChart(): Promise<FirstValueNotPieChart[]> {
	return axios.get(`${URL_FOUNDATION}/api/v2/additional-columns/first-value-not-pie-chart`).then(res => res.data);
}

export async function getClientCompanyAdditionalColumnsValues(companyId: number): Promise<CompanyAdditionalColumnValue[]> {
	return axios.get(`${URL_FOUNDATION}/api/v2/additional-columns/client-company/${companyId}`).then(res => res.data);
}

export async function getUserCategories(userId: number): Promise<Group[]> {
	return await axios.get(`${URL_FOUNDATION}/api/v2/additional-columns/groups/` + userId).then(res => res.data);
}

export async function syncUserCategories(userId: number, reportGroups: Group[]): Promise<void> {
	return await axios.put(`${URL_FOUNDATION}/api/v2/additional-columns/groups/` + userId, reportGroups);
}
